import React, { useMemo, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { storyChainOf, storyStats, isLinkedText, locateObject, locationLabel } from '../../lib/storyChain';
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Link2, AlertTriangle, Unlink } from 'lucide-react';
import { toast } from 'sonner';

/* ------------------------------------------------------------------------------------------------
 * Story Map — shown while a linked text box is selected. Two parts:
 *   1. StoryMapOverlay: on the page, every box of the chain gets a numbered badge and a dashed outline, with
 *      thread lines from the end of one box to the start of the next (reading order at a glance).
 *   2. StoryMapPanel: a compact card in the corner of the workspace listing the boxes (snippet, words), the story
 *      total and the overflow, with Previous / Next and click-to-jump.
 * ------------------------------------------------------------------------------------------------ */

/** The chain of the (single) selected linked text box, or null. */
export const useSelectedStory = (objects) => {
  const { state } = useEditor();
  const selectedId = state.selectedObjectIds.length === 1 ? state.selectedObjectIds[0] : (state.editingObjectId || null);
  return useMemo(() => {
    if (!selectedId) return null;
    const sel = objects.find((o) => o.id === selectedId);
    if (!isLinkedText(sel)) return null;
    const chain = storyChainOf(sel.id, objects);
    return chain.length > 1 ? { chain, selectedId: sel.id, index: chain.findIndex((o) => o.id === sel.id) } : null;
  }, [selectedId, objects]);
};

const ACCENT = 'hsl(var(--primary))';

/** Point on a box edge in page coordinates: bottom-right for the outgoing thread, top-left for the incoming one. */
const outPoint = (o) => ({ x: o.x + o.width, y: o.y + o.height });
const inPoint = (o) => ({ x: o.x, y: o.y });

export const StoryMapOverlay = ({ story, zoom, editingId, visibleIds = null }) => {
  const { dispatch } = useEditor();
  if (!story) return null;
  const { chain, selectedId } = story;
  // a story may span pages: only the boxes rendered on this page get badges / threads (threads only between neighbours both here)
  const here = (box) => !visibleIds || visibleIds.has(box.id);
  const threads = chain.slice(0, -1).filter((box, i) => here(box) && here(chain[i + 1])).map((box) => {
    const i = chain.indexOf(box);
    const a = outPoint(box), b = inPoint(chain[i + 1]);
    const dx = (b.x - a.x) * zoom, dy = (b.y - a.y) * zoom;
    const c = Math.max(24, Math.min(80, Math.hypot(dx, dy) / 3));
    return { id: `${box.id}-${chain[i + 1].id}`, d: `M${a.x * zoom},${a.y * zoom} C${a.x * zoom + c},${a.y * zoom} ${b.x * zoom - c},${b.y * zoom} ${b.x * zoom},${b.y * zoom}` };
  });
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 996 }} data-testid="story-map-overlay" data-boxes={chain.length}>
      <svg className="absolute inset-0 overflow-visible" width="100%" height="100%" aria-hidden="true">
        <defs>
          <marker id="wp-story-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M0,0 L10,5 L0,10 Z" fill={ACCENT} /></marker>
        </defs>
        {threads.map((t) => <path key={t.id} d={t.d} fill="none" stroke={ACCENT} strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#wp-story-arrow)" opacity="0.85" data-testid="story-thread" />)}
      </svg>
      {chain.map((box, i) => {
        if (!here(box)) return null;
        const active = box.id === selectedId;
        const editing = box.id === editingId;
        return (
          <React.Fragment key={box.id}>
            {!active && !editing && (
              <div className="absolute rounded-[2px]" style={{ left: box.x * zoom - 2, top: box.y * zoom - 2, width: box.width * zoom + 4, height: box.height * zoom + 4, border: `1.5px dashed ${ACCENT}`, opacity: 0.7 }} data-testid={`story-box-outline-${i + 1}`} />
            )}
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); if (!active) dispatch({ type: 'SELECT_OBJECT', payload: { id: box.id } }); }}
              className={`absolute pointer-events-auto h-5 min-w-5 px-1.5 rounded-full text-[11px] font-bold leading-5 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? 'bg-primary text-primary-foreground' : 'bg-card text-primary border border-primary/60 hover:bg-primary/10'}`}
              style={{ left: box.x * zoom - 8, top: box.y * zoom - 10, zIndex: 2 }}
              title={`Story box ${i + 1} of ${chain.length}${active ? ' (selected)' : ' — click to select'}`}
              aria-label={`Story box ${i + 1} of ${chain.length}`}
              data-testid={`story-box-badge-${i + 1}`}
              data-active={active ? '1' : undefined}
            >{i + 1}</button>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const StoryMapPanel = ({ story, textFlow, offsetLeft = 12 }) => {
  const { dispatch, state, openDialog } = useEditor();
  const [collapsed, setCollapsed] = useState(false);
  const stats = useMemo(() => (story ? storyStats(story.chain, textFlow) : null), [story, textFlow]);
  if (!story || !stats) return null;
  const { chain, index } = story;
  const go = (i) => { const box = chain[i]; if (box) dispatch({ type: 'SELECT_OBJECT_ANYWHERE', payload: box.id }); };
  const whereOf = (box) => locateObject(box.id, state.pages, state.scratch);
  const spansPages = new Set(chain.map((b) => { const l = whereOf(b); return l ? (l.scratch ? 'scratch' : l.pageIndex) : 'x'; })).size > 1;
  const pageTag = (box) => {
    const loc = whereOf(box);
    if (!loc) return '';
    if (loc.scratch) return 'Scratch area';
    return loc.pageIndex === state.currentPageIndex ? 'this page' : locationLabel(loc, state.pages);
  };
  const unlinkAfterSelected = () => {
    const box = chain[index];
    if (!box?.properties?.linkNextId) return;
    dispatch({ type: 'UNLINK_TEXTBOX', payload: box.id });
    toast.success(`Story split after box ${index + 1} — the text that flowed on stays in box ${index + 2}`);
  };
  return (
    <div className="absolute bottom-3 w-[268px] max-w-[calc(100%-24px)] rounded-md border border-border bg-popover/95 backdrop-blur-md shadow-lg text-popover-foreground" style={{ zIndex: 60, left: offsetLeft }} data-testid="story-map-panel" data-boxes={chain.length}>
      <div className="flex items-center gap-2 px-2.5 h-8 border-b border-border">
        <Link2 className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden="true" />
        <span className="text-[12px] font-semibold truncate">Linked story</span>
        <span className="text-[11px] text-muted-foreground truncate" data-testid="story-map-summary">{chain.length} boxes · {stats.words} word{stats.words === 1 ? '' : 's'}{spansPages ? ' · several pages' : ''}</span>
        <button type="button" onClick={() => setCollapsed((v) => !v)} className="ml-auto h-6 w-6 rounded-sm inline-flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={collapsed ? 'Expand story map' : 'Collapse story map'} aria-expanded={!collapsed} data-testid="story-map-toggle">{collapsed ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button>
      </div>
      {!collapsed && (
        <>
          <ol className="max-h-[168px] overflow-y-auto py-1" data-testid="story-map-list">
            {stats.boxes.map((b, i) => {
              const active = i === index;
              return (
                <li key={b.id}>
                  <button type="button" onClick={() => go(i)} className={`w-full flex items-start gap-2 px-2.5 py-1 text-left hover:bg-accent/60 focus-visible:outline-none focus-visible:bg-accent ${active ? 'bg-primary/10' : ''}`} aria-current={active ? 'true' : undefined} data-testid={`story-map-item-${i + 1}`}>
                    <span className={`mt-0.5 h-4 min-w-4 px-1 rounded-full text-[10px] font-bold leading-4 text-center ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[12px] truncate ${b.snippet ? '' : 'italic text-muted-foreground'}`}>{b.snippet || 'empty — nothing flows here yet'}</span>
                      <span className="block text-[10px] text-muted-foreground">{b.words} word{b.words === 1 ? '' : 's'}{spansPages ? ` · ${pageTag(chain[i])}` : ''}{i === 0 ? ' · first box (holds the story)' : ''}{i === chain.length - 1 && i > 0 ? ' · last box' : ''}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          {stats.overflowWords > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-amber-700 dark:text-amber-300 border-t border-border" data-testid="story-map-overflow">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> <span className="flex-1">{stats.overflowWords} word{stats.overflowWords === 1 ? '' : 's'} still in overflow after the last box.</span>
              <button type="button" onClick={() => openDialog('autoflow', { objectId: chain[chain.length - 1].id })} className="shrink-0 h-5 px-1.5 rounded-sm border border-amber-500/40 bg-background text-[11px] font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="story-map-autoflow">Autoflow…</button>
            </div>
          )}
          <div className="flex items-center gap-1 px-2 h-8 border-t border-border">
            <button type="button" onClick={() => go(index - 1)} disabled={index <= 0} className="h-6 px-1.5 rounded-sm inline-flex items-center gap-1 text-[11px] hover:bg-accent disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="story-map-prev"><ChevronLeft className="h-3.5 w-3.5" /> Previous</button>
            <button type="button" onClick={() => go(index + 1)} disabled={index >= chain.length - 1} className="h-6 px-1.5 rounded-sm inline-flex items-center gap-1 text-[11px] hover:bg-accent disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="story-map-next">Next <ChevronRight className="h-3.5 w-3.5" /></button>
            <span className="ml-auto text-[11px] text-muted-foreground" data-testid="story-map-position">Box {index + 1} of {chain.length}</span>
            <button type="button" onClick={unlinkAfterSelected} disabled={!chain[index]?.properties?.linkNextId || !!state.editingObjectId} className="h-6 w-6 rounded-sm inline-flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" title="Break the link after this box" aria-label="Break the link after this box" data-testid="story-map-unlink"><Unlink className="h-3.5 w-3.5" /></button>
          </div>
        </>
      )}
    </div>
  );
};
